import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  Renderer2,
  Output,
  EventEmitter,
  ElementRef,
  OnInit,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import {
  AVAILABLEDOCUMENTTYPES,
  PAYMENTMETHODS,
} from "../../../document/document.data";

@Component({
  selector: "app-invoice-template1",
  templateUrl: "./invoice-template1.component.html",
  styleUrls: ["./invoice-template1.component.scss"],
})
export class InvoiceTemplate1Component
  implements OnChanges, AfterViewInit, OnInit
{
  @Input() invoiceData: any;
  @Input() lang: "en" | "ar" | "he" = "en";
  @Input() showInvoiceStop: boolean = false;
  @Input() invoiceId: number | null = null;
  @Output() showAgain = new EventEmitter<void>();
  @Input() userOptions: boolean = false;
  @Input() pdfLink: string | null = null;

  private previousLangClass: string | null = null;

  // cache translations for the invoice's selected language so we don't
  // change the application's global language when rendering the invoice
  private localTranslations: any = null;

  constructor(
    private translate: TranslateService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private http: HttpClient
  ) {}

  ngAfterViewInit(): void {
    this.updateBodyClass();
    // small DOM sanitization: replace any literal English strings that may have
    // been injected by the server (for example 'Billing system') with the
    // correct translation for the active language. We run this shortly after
    // view init to allow the dynamic content to settle.
    setTimeout(() => this.replaceLiteralBillingSystem(), 180);
  }

  /** Replace literal 'Billing system' text nodes inside this component with the translated value. */
  private replaceLiteralBillingSystem(): void {
    try {
      const translated =
        this.translateKeyLocal("invoice.billingSystem") || "מערכת חיובים";
      const root: HTMLElement = this.elRef?.nativeElement;
      if (!root) return;

      // Find elements whose trimmed text content exactly matches the English phrase
      // and replace their text with the translated version.
      // createTreeWalker expects at most three args in TS DOM lib: root, whatToShow, filter
      const filter = {
        acceptNode: (node: Node) => {
          // accept all text nodes so we can inspect them
          return NodeFilter.FILTER_ACCEPT;
        },
      } as NodeFilter;

      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        filter
      );
      const textsToUpdate: Text[] = [];
      let node: Text | null = walker.nextNode() as Text | null;
      while (node) {
        if (node.nodeValue && node.nodeValue.trim() === "Billing system") {
          textsToUpdate.push(node);
        }
        node = walker.nextNode() as Text | null;
      }

      textsToUpdate.forEach((t) => {
        t.nodeValue = translated;
      });
    } catch (e) {
      // ignore any errors during DOM sanitization
      // keep the invoice rendering as-is if something fails
      console.warn("replaceLiteralBillingSystem failed", e);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["lang"]) {
      this.updateBodyClass();
      // load translations for the requested invoice language into a local
      // cache. We intentionally DO NOT call translate.use() to avoid
      // changing the global application language.
      this.translate.getTranslation(this.lang).subscribe(
        (trans) => {
          this.localTranslations = trans || {};
          },
        (err) => {
          console.warn("[InvoiceTemplate1] getTranslation failed", err);
          this.localTranslations = null;
        }
      );
    }
  }

  ngOnInit(): void {
    // Load the specific invoice translations file
    try {
      this.http
        .get("assets/i18n/invoice_translations.updated_v2.json")
        .subscribe(
          (trans) => {
            this.localTranslations = trans || {};
            // Force change detection after translations are loaded to ensure correct language display
            setTimeout(() => {
              this.updateBodyClass();
            }, 0);
          },
          (err) => {
            console.error(
              `❌ [TEMPLATE1] Failed to load invoice_translations.updated_v2.json:`,
              err
            );
            this.localTranslations = null;
          }
        );
    } catch (e) {
      console.error(`💥 [TEMPLATE1] Error loading invoice translations:`, e);
      this.localTranslations = null;
    }
  }

  private updateBodyClass(): void {
    // add the language class to this component's root element so we don't
    // interfere with the whole application's styling
    const newClass = `lang-${this.lang}`;
    const root = this.elRef?.nativeElement as HTMLElement;
    if (!root) return;
    if (this.previousLangClass) {
      this.renderer.removeClass(root, this.previousLangClass);
    }
    this.renderer.addClass(root, newClass);
    this.previousLangClass = newClass;
  }

  get direction(): "ltr" | "rtl" {
    return ["ar", "he"].includes(this.lang) ? "rtl" : "ltr";
  }

  getInvoiceTypeName(): string {
    const currentType = AVAILABLEDOCUMENTTYPES.find(
      (type) => +type.id === +this.invoiceData?.invoice_type
    );
    if (!currentType) return "";
    return (
      currentType[`name_${this.lang}` as "name_en" | "name_ar" | "name_he"] ||
      currentType.name_en
    );
  }

  getCompanyName(): string {
    return (
      this.invoiceData?.company?.[`company_name_${this.lang}`] ||
      this.invoiceData?.company?.company_name_en ||
      this.invoiceData?.company_name ||
      ""
    );
  }

  getCustomerName(): string {
    return (
      this.invoiceData?.customer?.[`customer_name_${this.lang}`] ||
      this.invoiceData?.customer?.customer_name_en ||
      this.invoiceData?.customer_name ||
      ""
    );
  }

  getCompanyVat(): string {
    return (
      this.invoiceData?.company?.tax_register ||
      this.invoiceData?.company?.vat ||
      this.invoiceData?.company_vat ||
      this.invoiceData?.tax_register ||
      ""
    );
  }

  getCustomerVat(): string {
    return (
      this.invoiceData?.customer?.customer_vat_number ||
      this.invoiceData?.customer_vat_number ||
      ""
    );
  }

  getCompanyPhone(): string {
    return (
      this.invoiceData?.company?.company_phone ||
      this.invoiceData?.company_phone ||
      ""
    );
  }

  getCompanyAddress(): string {
    const addressKey = `company_address_${this.lang}`;
    return (
      this.invoiceData?.company?.[addressKey] ||
      this.invoiceData?.[addressKey] ||
      this.invoiceData?.company?.address ||
      ""
    );
  }

  getFormattedDate(): string {
    if (!this.invoiceData?.invoice_date) return "";
    const date = new Date(this.invoiceData.invoice_date);
    return date.toLocaleDateString("en-GB");
  }

  formatNumber(value: any): string {
    if (value == null || value === "") return "0.00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  getConfirmationNumber(): { remainingDigits: string; last9Digits: string } {
    const confirmNumber = this.invoiceData?.confirmation_number || "";
    if (confirmNumber.length <= 9) {
      return { remainingDigits: "", last9Digits: confirmNumber };
    }
    return {
      remainingDigits: confirmNumber.slice(0, -9),
      last9Digits: confirmNumber.slice(-9),
    };
  }

  getPaymentMethodName(type: number): string {
    // Handle deduction type (5) separately
    if (type === 5) {
      if (this.lang === 'en') return 'Deduction';
      if (this.lang === 'ar') return 'خصم';
      return 'ניכוי';
    }
    
    const method = PAYMENTMETHODS.find((m) => m.id === type);
    return method
      ? method[`name_${this.lang}` as "name_en" | "name_ar" | "name_he"]
      : "";
  }

  /**
   * Check if the current document is a receipt
   * Receipt types: 400 (Receipt) and 430 (Cancelled Receipt)
   */
  isReceipt(): boolean {
    const invoiceType = String(this.invoiceData?.invoice_type || '');
    return invoiceType === '400' || invoiceType === '430';
  }

  /**
   * Resolve a translation key using the locally cached translations for the
   * invoice language. If translation is not available locally, fall back to
   * global translate service.
   */
  translateKeyLocal(key: string, params?: Record<string, any>): string {
    try {
      // helper to pick nested value from the translations object
      const getNested = (obj: any, path: string) => {
        if (!obj) return undefined;
        return path
          .split(".")
          .reduce(
            (acc: any, part: string) =>
              acc && acc[part] !== undefined ? acc[part] : undefined,
            obj
          );
      };

      let value: any = undefined;
      if (this.localTranslations) {
        value = getNested(this.localTranslations, key);
        }

      // If value is an object with language keys, extract the correct language
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const langValue =
          value[this.lang] || value["en"] || value["ar"] || value["he"];
        if (langValue) {
          value = langValue;
        }
      }

      // If local translations are not available yet, DON'T fall back to global translate service
      // This prevents mixed language display during initialization
      if (value === undefined) {
        // Return the key itself or a placeholder to avoid mixed language display
        return key;
      }

      if (!value) {
        return key;
      }

      // simple interpolation for params using {{key}} or {{ key }} syntax
      if (params && typeof value === "string") {
        Object.keys(params).forEach((p) => {
          const re = new RegExp("{{\\s*" + p + "\\s*}}", "g");
          value = value.replace(re, String(params[p]));
        });
      }
      return value;
    } catch (e) {
      console.error(
        `💥 [TEMPLATE1] Error in translateKeyLocal for key "${key}":`,
        e
      );
      // Don't fall back to global translate service to avoid mixed languages
      return key;
    }
  }

  getPaymentAmountIncludingVat(): number {
    // For INVOICES: Use the standard calculation (payment_amount + VAT)
    // Do NOT use receiptsDetails for invoice totals - they are for payment allocation only
    if (!this.isReceipt()) {
      // Fallback to provided value first
      const providedValue = parseFloat(
        this.invoiceData?.payment_amount_including_vat
      );
      if (!isNaN(providedValue) && providedValue > 0) {
        return providedValue;
      }

      // For invoices: Total = payment_amount + VAT
      const paymentAmount = parseFloat(this.invoiceData?.payment_amount) || 0;
      const vatAmount = parseFloat(this.invoiceData?.all_vat_amount) || 0;
      return paymentAmount + vatAmount;
    }

    // For RECEIPTS: Sum up all payment amounts from receiptsDetails (excluding deductions)
    if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails) && this.invoiceData.receiptsDetails.length > 0) {
      const totalFromReceipts = this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
        const paymentType = Number(receipt.payment_type) || 0;
        const amount = parseFloat(receipt.amount) || 0;
        // Include all payment types except deductions (type 5)
        if (paymentType !== 5) {
          return sum + amount;
        }
        return sum;
      }, 0);
      
      // For receipts, ADD deduction instead of subtracting (deduction is additional amount)
      const deduction = this.getDeductionAmount();
      // For receipts: Total = Payments + Deduction
      const finalTotal = totalFromReceipts + deduction;
      if (finalTotal > 0) {
        return finalTotal;
      }
      // If deduction makes total negative or zero, return the payments total before deduction
      if (totalFromReceipts > 0) {
        return totalFromReceipts;
      }
    }

    // Fallback for receipts
    const providedValue = parseFloat(
      this.invoiceData?.payment_amount_including_vat
    );
    if (!isNaN(providedValue) && providedValue > 0) {
      return providedValue;
    }

    // Final fallback
    const paymentAmount = parseFloat(this.invoiceData?.payment_amount) || 0;
    const vatAmount = parseFloat(this.invoiceData?.all_vat_amount) || 0;
    return paymentAmount + vatAmount;
  }

  /**
   * Get deduction amount from receiptsDetails or deduction_from_source
   */
  getDeductionAmount(): number {
    // First check if deduction is in receiptsDetails (payment_type 5)
    if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails)) {
      const deductionFromReceipts = this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
        if (Number(receipt.payment_type) === 5) {
          return sum + (parseFloat(receipt.amount) || 0);
        }
        return sum;
      }, 0);
      
      if (deductionFromReceipts > 0) {
        return deductionFromReceipts;
      }
    }
    
    // Fallback to deduction_from_source field
    const deductionFromSource = parseFloat(this.invoiceData?.deduction_from_source || '0');
    return deductionFromSource || 0;
  }

  /**
   * Get total payments before deduction
   */
  getTotalPaymentsBeforeDeduction(): number {
    if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails)) {
      return this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
        const paymentType = Number(receipt.payment_type) || 0;
        const amount = parseFloat(receipt.amount) || 0;
        // Sum all payment types except deductions (type 5)
        if (paymentType !== 5) {
          return sum + amount;
        }
        return sum;
      }, 0);
    }
    
    // Fallback to cash_amount or total_amount
    const cashAmount = parseFloat(this.invoiceData?.cash_amount || '0');
    const checksAmount = parseFloat(this.invoiceData?.checks_amount || '0');
    const bankAmount = parseFloat(this.invoiceData?.bank_payment_amount || '0');
    const creditAmount = parseFloat(this.invoiceData?.credit_amount || '0');
    
    return cashAmount + checksAmount + bankAmount + creditAmount;
  }

  /**
   * Check if deduction exists in receiptsDetails
   */
  hasDeductionInReceiptsDetails(): boolean {
    if (!this.invoiceData?.receiptsDetails || !Array.isArray(this.invoiceData.receiptsDetails)) {
      return false;
    }
    return this.invoiceData.receiptsDetails.some((receipt: any) => Number(receipt.payment_type) === 5);
  }

  getBankName(bank: any): string {
    if (!bank) return "";
    if (typeof bank === "string") return bank;
    if (typeof bank === "object") {
      return (
        bank.bank_name ||
        bank.name ||
        bank.bank_name_en ||
        bank.bank_name_he ||
        bank.bank_name_ar ||
        ""
      );
    }
    return "";
  }

  getVatPercentage(): number {
    const vatPercentage =
      this.invoiceData?.vat_percentage ||
      this.invoiceData?.vat_rate ||
      this.invoiceData?.vatPercentage ||
      this.invoiceData?.lines?.[0]?.vat_rate ||
      this.invoiceData?.soldItems?.[0]?.vat_rate ||
      0;
    return parseFloat(vatPercentage) || 0;
  }

  printPdfLink(): void {
    if (this.pdfLink) {
      window.open(this.pdfLink, "_blank");
    }
  }

  showAgainInvoiceStop(): void {
    this.showAgain.emit();
  }

  /**
   * Helper method to get VAT toggle display text
   */
  getVatToggleText(): string {
    return this.translateKeyLocal("documentLines.incVat") || "VAT Included";
  }

  /**
   * Helper method to get discount type display text
   */
  getDiscountTypeText(): string {
    return (
      this.translateKeyLocal("documentLines.discountTypePercentage") ||
      "Percentage"
    );
  }

  // Complex customer VAT/ID display logic - matches Handlebars template exactly
  getCustomerVatIdLines(): string[] {
    const customerVatNumber =
      this.invoiceData?.customer?.customer_vat_number ||
      this.invoiceData?.customer_vat_number ||
      "";
    const customerIdNumber =
      this.invoiceData?.customer?.customer_id_number ||
      this.invoiceData?.customer_id_number ||
      "";
    const customerType =
      this.invoiceData?.customer?.customer_type ||
      this.invoiceData?.customer_type ||
      6;

    const lines: string[] = [];

    // {{#if data.customer_vat_number}}
    if (customerVatNumber) {
      // {{#ifNotEquals data.customer_vat_number '0'}}
      if (customerVatNumber !== "0") {
        // {{#if data.customer_id_number}}
        if (customerIdNumber) {
          // {{#ifNotEquals data.customer_vat_number data.customer_id_number}}
          if (customerVatNumber !== customerIdNumber) {
            // Different numbers - show both
            // {{#ifEquals data.customer_type 5}}
            if (customerType === 5) {
              // Individual customer
              lines.push(`${this.getCustomerIdLabel()} ${customerIdNumber}`);
              lines.push(
                `${this.getBusinessLicenseLabel()} ${customerVatNumber}`
              );
            } else {
              // Company customer
              lines.push(`${this.getCompanyNumberLabel()} ${customerIdNumber}`);
              lines.push(
                `${this.getBusinessLicenseLabel()} ${customerVatNumber}`
              );
            }
          } else {
            // Same numbers
            // {{#ifEquals data.customer_type 5}}
            if (customerType === 5) {
              // Individual customer - same numbers
              lines.push(
                `${this.getCustomerIdBusinessLabel()} ${customerIdNumber}`
              );
            } else {
              // Company customer - same numbers
              lines.push(
                `${this.getCompanyBusinessLabel()} ${customerIdNumber}`
              );
            }
          }
        } else {
          // No customer_id_number - show VAT only (company format)
          lines.push(`${this.getCompanyBusinessLabel()} ${customerVatNumber}`);
        }
      }
    } else {
      // No customer_vat_number or it's '0' - show ID only if exists
      // {{#if data.customer_id_number}}
      if (customerIdNumber) {
        lines.push(`${this.getCustomerIdLabel()} ${customerIdNumber}`);
      }
    }

    return lines;
  }

  // Helper methods for labels based on language
  private getCustomerIdLabel(): string {
    if (this.lang === "en") return "ID Number";
    if (this.lang === "ar") return "رقم الهوية";
    return "ת.ז.";
  }

  private getCompanyNumberLabel(): string {
    if (this.lang === "en") return "Company Number";
    if (this.lang === "ar") return "رقم الشركة";
    return "ח.פ.";
  }

  private getBusinessLicenseLabel(): string {
    if (this.lang === "en") return "Business License";
    if (this.lang === "ar") return "مشتغل مرخص";
    return "עוסק מורשה";
  }

  private getCustomerIdBusinessLabel(): string {
    if (this.lang === "en") return "ID / Licensed Business";
    if (this.lang === "ar") return "هوية رقم / مشتغل مرخص";
    return "ת.ז. / עוסק מורשה";
  }

  private getCompanyBusinessLabel(): string {
    if (this.lang === "en") return "Company / Licensed Business";
    if (this.lang === "ar") return "شركة رقم / مشتغل مرخص";
    return "ת.ז. / עוסק מורשה";
  }

  // Get company type in parentheses format based on language
  getCompanyTypeInParentheses(): string {
    const companyType = this.invoiceData?.company?.companyType;
    if (!companyType?.type_name_ar) return '';
    
    const typeNameAr = companyType.type_name_ar;
    
    // Map Arabic type names to display format based on language
    const typeMapping: { [key: string]: { [key: string]: string } } = {
      'شركة': {
        'ar': 'شركة',
        'he': 'חברה',
        'en': 'Company'
      },
      'مشتغل مرخص': {
        'ar': 'مشتغل مرخص',
        'he': 'עוסק מורשה',
        'en': 'Licensed Dealer'
      },
      'مشتغل معفي': {
        'ar': 'مشتغل معفي',
        'he': 'עוסק פטור',
        'en': 'Exempt Dealer'
      },
      'جمعيه': {
        'ar': 'جمعية',
        'he': 'עמותה / מלכ"ר',
        'en': 'Association'
      }
    };
    
    const languageMapping = typeMapping[typeNameAr];
    if (languageMapping) {
      return languageMapping[this.lang] || languageMapping['ar'];
    }
    
    // Default: return only current language (without parentheses for Arabic)
    return this.lang === 'ar' ? typeNameAr : typeNameAr;
  }

  /**
   * Get company type display text based on type_name_ar from API response
   */
  getCompanyTypeDisplayText(): string {
    const typeNameAr = this.invoiceData?.company?.companyType?.type_name_ar;
    if (!typeNameAr) {
      return ""; // Return empty string if no company type data
    }

    // Mapping table for company type display text
    const companyTypeMapping: { [key: string]: string } = {
      "جمعيه": "עמותה / מלכ\"ר (جمعية)",
      "شركة": "חברה (شركة)",
      "مشتغل مرخص": "עוסק מורשה (مشتغل مرخص)",
      "مشتغل معفي": "עוסק פטור (مشتغل معفي)"
    };

    const result = companyTypeMapping[typeNameAr] || typeNameAr;
    return result;
  }
}
